import PropTypes from 'prop-types'
import React from 'react'
import styled, { css } from 'styled-components'
import { connect } from 'react-redux'
import { Button } from 'components/styled/button'
import { mapLanguageToComponentProps } from 'i18n'
import { Flex } from 'components/styled/containers/flex'
import { Box } from 'components/styled/containers/box'

const propTypes = {
  button: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  i18n: PropTypes.shape({
    webcam: PropTypes.object.isRequired,
  }).isRequired,
  onRecord: PropTypes.func.isRequired,
  onStop: PropTypes.func,
  onRemove: PropTypes.func,
  onSubmit: PropTypes.func,
  hideButtons: PropTypes.bool,
  disableStop: PropTypes.bool,
  disableRecord: PropTypes.bool,
  hasVideo: PropTypes.bool,
  isUpload: PropTypes.bool,
  isEmbedded: PropTypes.bool,
  state: PropTypes.string,
  showRetakeAndRemove: PropTypes.bool,
}

const defaultProps = {
  button: '',
  children: undefined,
  hideButtons: false,
  disableStop: false,
  disableRecord: false,
  hasVideo: false,
  isUpload: false,
  isEmbedded: false,
  onStop: undefined,
  onRemove: undefined,
  onSubmit: undefined,
  state: '',
  showRetakeAndRemove: true,
}

const UploadStyle = css`
  position: relative;
  flex-direction: column;
  bottom: 0;
  width: 100%;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.space.s8};

  > div button {
    width: 100%;
  }
`

const StackedButtonStyle = css`
  flex-direction: column;

  button {
    width: 100%;
  }
`

const EmbedStackedButtonStyle = css`
  ${StackedButtonStyle};
  position: absolute;
  padding: 0 ${({ theme }) => theme.space.s8};
  top: -225px;
  height: ${({ state, $isUpload }) =>
    state === 'pause' && !$isUpload ? '200px' : 'auto'};
  button {
    z-index: 1000;
    position: relative;
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    top: -215px;
  }
`

const RetakeAndRemove = styled(Flex)`
  width: 100%;
  ${StackedButtonStyle};

  ${({ theme }) => theme.mediaQuery.tablet} {
    flex-direction: row;

    button {
      width: auto;
    }
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    ${StackedButtonStyle};
  }

  && {
    ${({ $isEmbedded }) => $isEmbedded && EmbedStackedButtonStyle};
    ${({ $isUpload, $isEmbedded }) => $isUpload && !$isEmbedded && UploadStyle};
  }
`

const RecorderButtonsContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  position: relative;

  button span {
    white-space: normal;
  }
`

const $EmbeddedStyle = css`
  margin-top: ${({ theme }) => theme.space.s4};
  position: absolute;
  bottom: ${({ theme }) => theme.space.s16};

  button {
    z-index: 1000;
    position: relative;
  }
`

const StopButton = styled(Button)`
  width: 100%;
  display: flex;

  justify-content: center;

  ${({ isEmbedded }) => isEmbedded && $EmbeddedStyle}
`

const PreviewViewButtons = styled.div`
  flex-direction: column;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: ${({ theme, $isEmbedded }) => !$isEmbedded && theme.space.s12};
  margin-bottom: ${({ theme, $isEmbedded }) => !$isEmbedded && theme.space.s12};

  ${({ $isUpload }) => $isUpload && UploadStyle};

  ${({ theme }) => theme.mediaQuery.phone} {
    row-gap: ${({ theme }) => theme.space.s8};
  }
`

const SubmitButtonContainer = styled(Flex)`
  position: relative;
  width: 100%;
  justify-content: center;
  flex-direction: column;
  bottom: 0;
  align-items: center;
  row-gap: ${({ theme }) => theme.space.s8};
  margin-top: ${({ $isUpload, theme }) => ($isUpload ? 0 : theme.space.s12)};
`

const PhoneStyle = css`
  ${({ theme }) => theme.mediaQuery.phone} {
    position: relative;
    bottom: 0;
    width: 100%;

    > div button {
      width: 100%;
    }
  }
`

const OverlayButtonContainer = styled.div`
  z-index: 1000;
  position: absolute;
  bottom: ${({ theme, $isEmbedded }) =>
    $isEmbedded ? theme.space.s16 : theme.space.s24};

  ${({ $isUpload }) => $isUpload && UploadStyle};

  ${({ $isEmbedded }) => !$isEmbedded && PhoneStyle}
`
export function RecorderButtons(props) {
  const {
    onSubmit,
    i18n,
    onRecord,
    onStop,
    onRemove,
    hideButtons,
    button,
    disableStop,
    disableRecord,
    hasVideo,
    isUpload,
    isEmbedded,
    state,
    showRetakeAndRemove,
  } = props

  if (hideButtons) {
    return null
  }

  const showStopOnEmbedded =
    isEmbedded && (state === 'countdown' || state === 'record')
  return (
    <RecorderButtonsContainer
      className="webcam-buttons"
      $isEmbedded={isEmbedded}
    >
      <OverlayButtonContainer $isEmbedded={isEmbedded}>
        {((button === 'record' && !hasVideo) || button === 'error') && (
          <Button
            disabled={disableRecord}
            onClick={() => onRecord()}
            icon="radio_button_checked"
            size={isEmbedded ? 'small' : 'default'}
            text={i18n.webcam.record}
          />
        )}
      </OverlayButtonContainer>
      {hasVideo && (isEmbedded || (!isEmbedded && button !== 'stop')) && (
        <PreviewViewButtons $isEmbedded={isEmbedded} $isUpload={isUpload}>
          {showRetakeAndRemove && (
            <RetakeAndRemove
              justifyContent="space-between"
              $isUpload={isUpload}
              $isEmbedded={isEmbedded}
              state={state}
            >
              {!isUpload && (
                <Button
                  size={isEmbedded ? 'small' : 'default'}
                  useConfirm={isEmbedded}
                  variant="secondary"
                  onClick={() => onRecord()}
                  icon="undo"
                  text={i18n.webcam.retake}
                />
              )}
              {onRemove && (
                <Button
                  size={isEmbedded ? 'small' : 'default'}
                  useConfirm={isEmbedded}
                  variant="secondary"
                  onClick={() => onRemove()}
                  icon="delete"
                  text={i18n.webcam.remove}
                />
              )}
            </RetakeAndRemove>
          )}
          {onSubmit && (
            <SubmitButtonContainer
              className="submit-button-container"
              $isUpload={isUpload}
            >
              <Button
                icon="send"
                text={i18n.webcam.submit}
                onClick={onSubmit}
                disabled={disableRecord}
              />
            </SubmitButtonContainer>
          )}
        </PreviewViewButtons>
      )}
      {button === 'stop' &&
        !isUpload &&
        onStop &&
        (!isEmbedded || showStopOnEmbedded) && (
          <StopButton
            mt="s12"
            icon="stop"
            size={isEmbedded ? 'small' : 'default'}
            isEmbedded={isEmbedded}
            text={i18n.webcam.stop} // same button is used for recording and play stopping with draft videos. maybe different texts /stop playback /stop recording
            onClick={() => onStop()}
            disabled={disableStop}
          />
        )}
    </RecorderButtonsContainer>
  )
}

RecorderButtons.propTypes = propTypes
RecorderButtons.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps())(RecorderButtons)
